// Transport + domain types for the "Empresa" dashboard filters panel
// (Informes/Asistencia Dashboard, ASIS-5638 / REXASIS-001).
//
// Field names/casing MUST match the backend JSON (camelCase) exactly. The backend
// serializes with JsonNamingPolicy.CamelCase, so the C# camelCase properties reach
// the wire unchanged.

import type { LC_PagedResult } from '@/api/shared';

// GET /attendance/company-dashboard/filters --------------------------------------

export interface LC_CompanyDashboardSectionOut {
  code: string;
  label: string;
  isDefault: boolean;
}

export interface LC_CompanyDashboardCompanyOut {
  companyId: number;
  companyName: string;
}

export interface LC_GetCompanyDashboardFiltersOut {
  sections: LC_CompanyDashboardSectionOut[];
  companies: LC_CompanyDashboardCompanyOut[];
}

// GET /attendance/company-dashboard/filters/areas ---------------------------------
// Sin query params: el catálogo de Área depende únicamente de la empresa de sesión,
// independiente de qué empresa esté seleccionada en el filtro Empresa — por eso no
// se recarga cuando cambia esa selección.
//
// Catalog API (patrón especial, ver frontend/CLAUDE.md "Catalog APIs"): el backend
// (LC_GetCompanyDashboardAreasListOut { areas: [...] }) tiene una sola propiedad de
// tipo lista, así que ResponseEnvelopeHelper.TryExtractCatalogArray la aplana y el
// envelope llega como data: [...] directamente, NO como data: { areas: [...] }.

export interface LC_CompanyDashboardAreaOut {
  areaId: number;
  areaName: string;
}

export type LC_GetCompanyDashboardAreasOut = LC_CompanyDashboardAreaOut[];

// GET /attendance/company-dashboard/filters/employees ----------------------------

export interface LC_CompanyDashboardEmployeeOut {
  employeeId: number;
  employeeName: string;
  employeeTaxIdentificationNumber: string;
  companyId: number;
}

// Domain params — the hook works with number[]; the service converts to CSV.
// Omitted/[] companyIds/areaIds => no filter on that dimension (returns every
// employee in scope for the remaining filters).
export interface GetCompanyDashboardEmployeesParams {
  companyIds?: number[];
  areaIds?: number[];
  page?: number;
  pageSize?: number;
  searchText?: string | null;
  orderBy?: string | null;
  orderDirection?: 'ASC' | 'DESC' | null;
  // Selected employee IDs to validate against Empresa/Área (universe-wide, unpaginated).
  // Only sent when pruning on a dimensional filter change (mirrors
  // GetEmployeesFilteredParams.employeeIds in employeeFiltered.types.ts).
  employeeIds?: number[];
}

// Response DTO — apiClient flattens the paginated envelope into LC_PagedResult and
// surfaces sibling fields (e.g. validatedSelection) alongside data + pagination.
export type LC_GetCompanyDashboardEmployeesOut = LC_PagedResult<LC_CompanyDashboardEmployeeOut> & {
  validatedSelection?: number[] | null;
};

// Page size for real (incremental) pagination of the Colaboradores selector — same
// size used by the analogous /employees/filtered dimensional-filter pattern
// (EMPLOYEE_FILTER_PAGE_SIZE). Replaces the previous one-shot 1000-row catalog load,
// which silently pruned selections living outside that first page (code review
// finding, ASIS-5638 / PR #374).
export const COMPANY_DASHBOARD_EMPLOYEES_PAGE_SIZE = 200;

// Domain models — PascalCase singular, decoupled from the wire shape --------------

export interface CompanyDashboardSection {
  code: string;
  label: string;
  isDefault: boolean;
}

export interface CompanyDashboardCompany {
  id: number;
  name: string;
}

export interface CompanyDashboardArea {
  id: number;
  name: string;
}

export interface CompanyDashboardEmployee {
  id: number;
  name: string;
  taxIdentificationNumber: string;
  companyId: number;
}

export interface CompanyDashboardFilters {
  sections: CompanyDashboardSection[];
  companies: CompanyDashboardCompany[];
}

export interface CompanyDashboardEmployeesPagination {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface CompanyDashboardEmployeesPage {
  items: CompanyDashboardEmployee[];
  pagination: CompanyDashboardEmployeesPagination;
  // Subset of the requested employeeIds that still satisfy the Empresa/Área filters,
  // evaluated by the backend against the whole universe (not just this page).
  validatedSelection: number[];
}
